import {
  OutlinedSelect,
  Select,
  TextInput,
  TextOutlinedInput,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParameters } from '../../hooks/useParameters';
import { TranslationsKeys } from '../../locales/translations';
import { IdentificationSearchFilterValue } from './types';

type IdentificationSearchFilterProps = {
  variant?: 'default' | 'outlined';
  value?: Partial<IdentificationSearchFilterValue>;
  defaultValue?: Partial<IdentificationSearchFilterValue>;
  maxWidth?: number | string;
  onChange?: (value: IdentificationSearchFilterValue) => void;
};

const IdentificationSearchFilter = ({
  variant = 'default',
  value,
  defaultValue,
  maxWidth = 255,
  onChange,
}: IdentificationSearchFilterProps) => {
  const { t } = useTranslation(TranslationsKeys.COMMON);
  const { identificationTypeOptions, countryOptions } = useParameters();

  const [optionsKey, setOptionsKey] = useState<number>(1);
  const [identificationType, setIdentificationType] = useState<string | null>(
    null,
  );

  const [identificationCountry, setIdentificationCountry] = useState<
    string | null
  >(null);
  const [identificationDocument, setIdentificationDocument] = useState<string>(
    '',
  );

  const requiresCountry = useMemo(() => {
    const finded = identificationTypeOptions.find(
      (opt) => String(opt.value) === identificationType,
    );

    return finded && !finded?.countryId;
  }, [identificationTypeOptions, identificationType]);

  const SelectComponent = variant === 'default' ? Select : OutlinedSelect;
  const TextInputComponent =
    variant === 'default' ? TextInput : TextOutlinedInput;

  const handleChangeFilter = useCallback(
    (input: keyof IdentificationSearchFilterValue, value: string) => {
      const nextValues = {
        identificationDocument,
        identificationDocumentType: identificationType || '',
        identificationDocumentCountry: identificationCountry || '',
      };

      nextValues[input] = value;

      if (input === 'identificationDocumentType') {
        const finded = identificationTypeOptions.find(
          (opt) => String(opt.value) === value,
        );
        nextValues.identificationDocumentCountry = finded?.countryId || '';
      }

      !!onChange && onChange(nextValues);
    },
    [
      identificationDocument,
      identificationType,
      identificationCountry,
      identificationTypeOptions,
      onChange,
    ],
  );

  useEffect(() => {
    if (defaultValue) {
      setIdentificationType(defaultValue.identificationDocumentType || null);
      setIdentificationCountry(
        defaultValue.identificationDocumentCountry || null,
      );
      setIdentificationDocument(defaultValue.identificationDocument || '');
      setOptionsKey((prev) => prev + 1);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (value !== undefined) {
      setIdentificationType(value.identificationDocumentType || null);
      setIdentificationCountry(value.identificationDocumentCountry || null);
      setIdentificationDocument(value.identificationDocument || '');
      setOptionsKey((prev) => prev + 1);
    }
  }, [value]);

  return (
    <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
      <div style={{ width: '20%', minWidth: 220 }}>
        <SelectComponent
          key={`type-${optionsKey}`}
          name="identificationDocumentType"
          value={identificationTypeOptions.find(
            (opt) => String(opt.value) === String(identificationType),
          )}
          placeholder={t(
            'identificationSearch.filters.identificationDocumentType.placeholder',
          )}
          label={t(
            'identificationSearch.filters.identificationDocumentType.label',
          )}
          isClearable={false}
          options={identificationTypeOptions}
          onChange={(option) =>
            handleChangeFilter(
              'identificationDocumentType',
              String(option?.value),
            )
          }
        />
      </div>
      {!!requiresCountry && (
        <div style={{ width: '20%', minWidth: 183 }}>
          <SelectComponent
            key={`country-${optionsKey}`}
            name="identificationDocumentCountry"
            value={countryOptions.find(
              (opt) => String(opt.value) === String(identificationCountry),
            )}
            placeholder={t(
              'identificationSearch.filters.identificationDocumentCountry.placeholder',
            )}
            label={t(
              'identificationSearch.filters.identificationDocumentCountry.label',
            )}
            isClearable={false}
            options={countryOptions}
            onChange={(option) =>
              handleChangeFilter(
                'identificationDocumentCountry',
                String(option?.value),
              )
            }
          />
        </div>
      )}
      <div className="flex-fill" style={{ maxWidth: maxWidth }}>
        <TextInputComponent
          value={identificationDocument}
          name="identificationDocument"
          placeholder={t(
            'identificationSearch.filters.identificationDocument.placeholder',
          )}
          label={t('identificationSearch.filters.identificationDocument.label')}
          onChange={(e) =>
            handleChangeFilter('identificationDocument', String(e.target.value))
          }
        />
      </div>
    </div>
  );
};

export default IdentificationSearchFilter;
